import {addDays, parseISO} from 'date-fns';
import {formatInTimeZone, toZonedTime} from 'date-fns-tz';
import type {ProductInfo} from '@globus/image-overlay';
import type {Gsoa_ActionOffers_Document as Leaflet, Gsoa_ActionOffers_ActionOffer as ActionOffer, Gsoa_ActionOffers_StoreDocument as StoreDocument} from '~/openapi';
import {LEAFLET_MAX_DAYS_IN_FUTURE, TIMEZONE} from '#shared/constants';
import {GsoaParamBuilder} from '~/utils/GsoaParamBuilder';
import {formatPragueDate} from '~/utils/date';

function getPragueCalendarDate(date: Date | string): string {
	const parsed = typeof date === 'string' ? parseISO(date) : date;

	return formatInTimeZone(parsed, TIMEZONE, 'yyyy-MM-dd');
}

function getTodayInPrague(): string {
	return getPragueCalendarDate(new Date());
}

function getNowInPrague(): Date {
	return toZonedTime(new Date(), TIMEZONE);
}

export const MAIN_LEAFLET_CURRENT_ROUTE_NAME = 'letaky-aktualni';
export const MAIN_LEAFLET_FUTURE_ROUTE_NAME = 'letaky-budouci';

type LeafletValidityOffer = {
	offerType?: string | null;
	isComplete?: boolean;
	validFrom?: string | null;
	validTo?: string | null;
};

export function getLeafletGTMPageType(leaflet: Leaflet | undefined | null): string {
	if (!leaflet) {
		return 'leaflet_old';
	}

	const today = getTodayInPrague();
	const startDate = leaflet.validFrom ? getPragueCalendarDate(leaflet.validFrom) : null;
	const endDate = leaflet.validTo ? getPragueCalendarDate(leaflet.validTo) : null;

	if (startDate && startDate > today) {
		return 'leaflet_future';
	}
	if (endDate && endDate < today) {
		return 'leaflet_old';
	}

	return 'leaflet_actual';
}

export function isMainLeaflet(offer?: {offerType?: string | null; isComplete?: boolean} | null): boolean {
	const normalizedOfferType = offer?.offerType
		?.toLowerCase()
		.replace(/[\s_-]+/g, '');

	return offer?.isComplete === true && (normalizedOfferType === 'mainflyer' || normalizedOfferType === 'flyermain');
}

// When validities overlap (e.g. new leaflet starts on Tuesday, old one ends on Tuesday), the newest one is current
export function getCurrentMainLeaflet<T extends LeafletValidityOffer>(offers: T[]): T | null {
	return getActiveActionOffersList(offers)
		.filter(isMainLeaflet)
		.reduce<T | null>((newest, offer) => (
			!newest || parseISO(offer.validFrom!) > parseISO(newest.validFrom!) ? offer : newest
		), null);
}

// Pass currentMainLeafletId to link only the current main leaflet to the current leaflet route, others go to their slug
export function getMainLeafletRouteName(
	offer?: LeafletValidityOffer & {actionOfferId?: string | null} | null,
	currentMainLeafletId?: string | null,
): string | null {
	if (!isMainLeaflet(offer)) {
		return null;
	}

	if (isLeafletActive(offer)) {
		return currentMainLeafletId === undefined || offer?.actionOfferId === currentMainLeafletId
			? MAIN_LEAFLET_CURRENT_ROUTE_NAME
			: null;
	}

	if (isLeafletComing(offer)) {
		return MAIN_LEAFLET_FUTURE_ROUTE_NAME;
	}

	return null;
}

export function isLeafletActive(offer?: LeafletValidityOffer | null): boolean {
	if (!offer?.validFrom || !offer?.validTo) {
		return false;
	}

	const today = getTodayInPrague();
	const startDate = getPragueCalendarDate(offer.validFrom);
	const endDate = getPragueCalendarDate(offer.validTo);

	return startDate <= today && endDate >= today;
}

export function isLeafletComing(offer?: LeafletValidityOffer | null): boolean {
	if (!offer?.validFrom) {
		return false;
	}

	const today = getTodayInPrague();
	const startDate = getPragueCalendarDate(offer.validFrom);
	const maxDate = formatInTimeZone(
		addDays(getNowInPrague(), LEAFLET_MAX_DAYS_IN_FUTURE),
		TIMEZONE,
		'yyyy-MM-dd',
	);

	return startDate > today && startDate <= maxDate;
}

export function getActiveActionOffersList<T extends {validFrom?: string | null; validTo?: string | null}>(offers: T[]): T[] {
	return offers.filter(isLeafletActive);
}

export function getComingActionOffersList<T extends {validFrom?: string | null}>(offers: T[]): T[] {
	return offers.filter(isLeafletComing);
}

type PriceValidityDates = {
	validFrom: string;
	validTo: string;
};

function getProductSaleDates(productInfo?: ProductInfo | null): PriceValidityDates | null {
	if (!productInfo) {
		return null;
	}

	const {saleFrom, saleTo} = productInfo;
	if (typeof saleFrom === 'string' && saleFrom && typeof saleTo === 'string' && saleTo) {
		return {validFrom: saleFrom, validTo: saleTo};
	}

	return null;
}

function resolvePriceValidityDates(
	productInfo?: ProductInfo | null,
	leafletValidFrom?: string | null,
	leafletValidTo?: string | null,
): PriceValidityDates | null {
	const productDates = getProductSaleDates(productInfo);
	if (productDates) {
		return productDates;
	}

	if (leafletValidFrom && leafletValidTo) {
		return {validFrom: leafletValidFrom, validTo: leafletValidTo};
	}

	return null;
}

function formatPriceValidityDate(date: string, withYear = false): string {
	return formatPragueDate(date, withYear ? 'd. M. yyyy' : 'd. M.');
}

export function getPriceValidityText(validFrom: string, validTo: string): string | null {
	const today = getTodayInPrague();
	const startDate = getPragueCalendarDate(validFrom);
	const endDate = getPragueCalendarDate(validTo);

	if (endDate < today) {
		return `Cena byla platná do ${formatPriceValidityDate(validTo)}`;
	}

	if (startDate > today) {
		const crossYear = startDate.slice(0, 4) !== endDate.slice(0, 4);

		return `Cena bude platná od ${formatPriceValidityDate(validFrom, crossYear)} do ${formatPriceValidityDate(validTo, crossYear)}`;
	}

	return `Cena je platná do ${formatPriceValidityDate(validTo)}`;
}

export function getProductPriceValidityText(
	productInfo?: ProductInfo | null,
	leafletValidFrom?: string | null,
	leafletValidTo?: string | null,
): string | null {
	const dates = resolvePriceValidityDates(productInfo, leafletValidFrom, leafletValidTo);

	return dates ? getPriceValidityText(dates.validFrom, dates.validTo) : null;
}

// Take only leaflets that are active in the near future
//
// minDate (now) |           maxDate |
//               |                   |
// [=======]     |                   |               ❌
//           [=======]               |               ✅
//               |     [=======]     |               ✅
//               |               [=======]           ✅
//               |                   |     [=======] ❌
export function getLeafletsFilter() {
	const minDate = getNowInPrague();
	const maxDate = addDays(minDate, LEAFLET_MAX_DAYS_IN_FUTURE);

	return createLeafletsFilter(maxDate, minDate);
}

export function getMainLeafletsFilter() {
	const minDate = getNowInPrague();
	const maxDate = addDays(minDate, LEAFLET_MAX_DAYS_IN_FUTURE);

	return createLeafletsFilter(maxDate, minDate, GsoaParamBuilder.equal('offerType', 'MainFlyer'));
}

function createLeafletsFilter(maxDate: Date, minDate: Date, additionalFilter?: string) {
	const filters = [
		GsoaParamBuilder.lessOrEqual('validFrom', formatInTimeZone(maxDate, TIMEZONE, 'yyyy-MM-dd')),
		GsoaParamBuilder.greaterOrEqual('validTo', formatInTimeZone(minDate, TIMEZONE, 'yyyy-MM-dd')),
	];

	if (additionalFilter) {
		filters.unshift(additionalFilter);
	}

	return new GsoaParamBuilder()
		.add('filter', GsoaParamBuilder.AND(filters))
		.build();
}

export const normalizeLeafletDetail = (document: Leaflet): ActionOffer => {
	// Extract the top-level properties and the document-specific properties
	const {
		actionOfferId,
		actionOfferName,
		validFrom,
		validTo,
		offerType,
		modified,
		slug,
		pdfAsset,
		jpgAssets,
		jpgAssetsProcessed,
	} = document;

	// Construct the nested StoreDocument object
	// We only create it if at least one of the asset properties actually exists
	const hasAssets = pdfAsset || (jpgAssets && jpgAssets.length > 0) || (jpgAssetsProcessed && jpgAssetsProcessed.length > 0);

	const storeDocuments: StoreDocument[] | null = hasAssets
		? [
			{
				pdfAsset,
				jpgAssets,
				jpgAssetsProcessed,
			},
		]
		: null;

	// Return the mapped object matching the Gsoa_ActionOffers_ActionOffer signature
	return {
		actionOfferId,
		actionOfferName,
		validFrom,
		validTo,
		offerType,
		modified,
		slug,
		storeDocuments,
	};
};
